import React, { useState, useEffect, useCallback } from 'react';
import { useUrlState } from '../../../hooks/useUrlState.ts';
import { employeeAPI, PersonalUseAPI } from '../../../services/api.ts';
import { Layout } from '../../../components/Layout.tsx';
import FilterModal from '../../../components/FilterModal.tsx';
import dayjs from 'dayjs';
import {
  ActionIcon,
  Alert,
  Button,
  Group,
  Loader,
  Paper,
  Stack,
  Table,
  Text,
  TextInput,
} from '@mantine/core';
import { Info, Search } from 'lucide-react';
import EmployeeInfoModal from '../../../components/Modal/EmployeeInfo.tsx';
import {
  PageHeader,
  StatusBadge,
  TablePagination,
} from '../../../components/app/index.ts';
import { isUnsynced, recordRef } from '../../../lib/records.ts';

import { useNavigate } from 'react-router-dom';

export const PersonalUse = () => {
  const navigate = useNavigate();
  const [projects, setProjects] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string>('');
  // Kept in the address, so the list comes back as it was left.
  const [page, setPage] = useUrlState('page', 1);
  const [totalPages, setTotalPages] = useState(1);
  const [totalItems, setTotalItems] = useState(0);
  const [searchTerm, setSearchTerm] = useUrlState('search', '');
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [filteredProjects, setFilteredProjects] = useState<any[]>([]);
  const [filterByGroup, setFilterByGroup] = useUrlState('group', '');
  const [from, setFrom] = useUrlState('from', '', { resetPage: true });
  const [to, setTo] = useUrlState('to', '', { resetPage: true });
  const [filterDataByGroup, setFilterDataByGroup] = useState<any[]>([]);
  const [checkboxStates, setCheckboxStates] = useState(filterByGroup);
  const [dateFilter, setDateFilter] = useState<boolean>(false);
  const [selectedEmployee, setSelectedEmployee] = useState<number | null>(null);
  const [showEmployeeDetails, setShowEmployeeDetails] = useState(false);
  const [employeeDetails, setEmployeeDetails] = useState<any>(null);

  const checkboxProps = [
    { id: 'employee', label: 'Group of Employee' },
    { id: 'company', label: 'Group of Company' },
  ];

  // The popup's choices go into the address; the list follows the address.
  const handleApply = (
    startDate: string,
    endDate: string,
    checkboxState: any
  ) => {
    if (startDate && endDate) {
      setFrom(startDate);
      setTo(endDate);
    }
    if (checkboxState) setFilterByGroup(checkboxState);
    setIsModalOpen(false);
  };

  const fetchByDates = async (
    startDate: string,
    endDate: string,
    checkboxState: any
  ) => {
    if (checkboxState) {
      setFilterByGroup(checkboxState);
      setIsModalOpen(false);
    }
    try {
      setLoading(true);

      if (startDate && endDate) {
        const response = await PersonalUseAPI.getPersonalUseAPIFilter(
          page,
          50,
          startDate,
          endDate
        );
        if (response.data) {
          setProjects(response.data as unknown as any[]);
        }
        setDateFilter(true);
        setTotalPages(response.pagination?.totalPages || 1);
        setTotalItems(response.pagination?.totalPersonalUses || 0);
        setIsModalOpen(false);
      }
    } catch (err: any) {
      setError(err.message || 'Failed to fetch personal use records record');
    } finally {
      setLoading(false);
    }
  };

  const fetchProjects = useCallback(async () => {
    try {
      setLoading(true);
      const response = await PersonalUseAPI.getAllPersonalUse(page);
      if (response?.data) {
        setProjects(response.data as unknown as any[]);
        // console.log(response.data);

        setTotalPages(response.pagination?.totalPages || 1);
        setTotalItems(response.pagination?.totalPersonalUses || 0);
      }
    } catch (err: any) {
      setError(err.message || 'Failed to fetch sales data');
    } finally {
      setLoading(false);
    }
  }, [page]);

  useEffect(() => {
    if (from && to) fetchByDates(from, to, '');
    else fetchProjects();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [page, fetchProjects, from, to]);

  useEffect(() => {
    if (searchTerm.trim() === '') {
      setFilteredProjects(projects);
      return;
    }

    const lowerSearch = searchTerm.toLowerCase();
    const filteredData = projects.filter((project) => {
      // The Odoo id once there is one, the job id until then.
      const id = recordRef(project).toLowerCase();
      const companyName = project?.company?.name?.toLowerCase() || '';
      const employeeName = project?.employee?.name?.toLowerCase() || '';

      return (
        id.includes(lowerSearch) ||
        companyName.includes(lowerSearch) ||
        employeeName.includes(lowerSearch)
      );
    });
    setFilteredProjects(filteredData);
  }, [searchTerm, projects]);

  useEffect(() => {
    if (!filterByGroup) return;
    let groupedProjects: any[] = [];

    // Group by Employee, Territory, Company, or Customer
    if (filterByGroup === 'employee') {
      groupedProjects = projects.reduce((acc: any[], project) => {
        const employeeName = project.employee?.name || 'Unknown';
        const existingGroup = acc.find(
          (group) => group.employeeName === employeeName
        );

        if (existingGroup) {
          existingGroup.projects.push(project);
        } else {
          acc.push({ employeeName, projects: [project] });
        }

        return acc;
      }, []);
    }

    if (filterByGroup === 'company') {
      groupedProjects = projects.reduce((acc: any[], project) => {
        const companyName = project.company?.name || 'Unknown';
        const existingGroup = acc.find(
          (group) => group.companyName === companyName
        );

        if (existingGroup) {
          existingGroup.projects.push(project);
        } else {
          acc.push({ companyName, projects: [project] });
        }

        return acc;
      }, []);
    }

    setFilterDataByGroup(groupedProjects);
  }, [filterByGroup, projects]);

  const resetFilter = () => {
    setFilterByGroup('');
    setFilterDataByGroup([]);
    setSearchTerm('');
    setCheckboxStates('');
    setDateFilter(false);
    setFrom('');
    setTo('');
    fetchProjects();
  };

  useEffect(() => {
    if (selectedEmployee) {
      setShowEmployeeDetails(true);
      employeeAPI.details(selectedEmployee).then((res) => {
        if (res.data) {
          setEmployeeDetails(res.data);
        }
      });
      return () => {
        setShowEmployeeDetails(false);
        setEmployeeDetails(null);
      };
    }
  }, [selectedEmployee]);

  const COLUMNS = 8;

  // One personal use record. `showInfo` adds the button that opens the employee's details.
  const renderRow = (project: any, showInfo: boolean) => (
    <Table.Tr key={project._id}>
      <Table.Td>{recordRef(project)}</Table.Td>
      <Table.Td>{project.name}</Table.Td>
      <Table.Td>
        <Group gap={6} wrap="nowrap">
          {project.employee?.name}
          {showInfo && (
            <ActionIcon
              variant="subtle"
              color="gray"
              size="sm"
              aria-label="Employee details"
              onClick={() => setSelectedEmployee(project.employee?.id)}
            >
              <Info size={16} />
            </ActionIcon>
          )}
        </Group>
      </Table.Td>
      <Table.Td>
        {/* Queued or failed until it reaches Odoo, then Odoo's own state. */}
        <StatusBadge
          status={isUnsynced(project) ? project.status : project.odooStatus}
        />
        {project.syncError && (
          <Text size="xs" c="red" mt={4}>
            {project.syncError}
          </Text>
        )}
      </Table.Td>
      <Table.Td>{project.company?.name}</Table.Td>
      <Table.Td>{project?.personal_travel} KM</Table.Td>
      <Table.Td>{project?.personal_travel_amount?.toString()} PKR</Table.Td>
      <Table.Td>{dayjs(project.createdAt).format('DD/MM/YYYY HH:mm:ss')}</Table.Td>
    </Table.Tr>
  );

  return (
    <Layout>
      <PageHeader
        title="Personal Use Records"
        actions={
          <>
            <Button
              variant="light"
              onClick={() => navigate('/fleet/personal-use/cross-match')}
            >
              Cross-Match
            </Button>
            <Button onClick={() => setIsModalOpen(true)}>Filter</Button>
            {(filterDataByGroup.length > 0 || dateFilter) && (
              <Button color="red" variant="light" onClick={resetFilter}>
                Reset
              </Button>
            )}
          </>
        }
      />

      <Paper withBorder shadow="sm" p={{ base: 'md', sm: 'lg' }}>
        <Stack gap="md">
          <TextInput
            type="search"
            placeholder="Search Personal use records"
            leftSection={<Search size={16} />}
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.currentTarget.value)}
            onFocus={() => {
              setFilterByGroup('');
              setFilterDataByGroup([]);
            }}
          />

          {error && (
            <Alert
              color="red"
              variant="light"
              withCloseButton
              onClose={() => setError('')}
            >
              {error}
            </Alert>
          )}

          {loading ? (
            <Group justify="center" h={160}>
              <Loader />
            </Group>
          ) : (
            <>
              <Table.ScrollContainer minWidth={1000}>
                <Table withTableBorder>
                  <Table.Thead>
                    <Table.Tr>
                      <Table.Th>ID</Table.Th>
                      <Table.Th>Name</Table.Th>
                      <Table.Th>Employee</Table.Th>
                      <Table.Th>Odoo Status</Table.Th>
                      <Table.Th>Company</Table.Th>
                      <Table.Th>Total Travelled (KM)</Table.Th>
                      <Table.Th>Travelled Amount (PKR)</Table.Th>
                      <Table.Th>Created At</Table.Th>
                    </Table.Tr>
                  </Table.Thead>
                  <Table.Tbody>
                    {filteredProjects.length > 0 &&
                      filterDataByGroup.length === 0 &&
                      filteredProjects.map((project) => renderRow(project, true))}

                    {filterDataByGroup.map((group, index) => (
                      <React.Fragment key={index}>
                        <Table.Tr>
                          <Table.Td
                            colSpan={COLUMNS}
                            fw={600}
                            bg="var(--mantine-color-default-hover)"
                          >
                            {group.employeeName || group.companyName}
                          </Table.Td>
                        </Table.Tr>
                        {group.projects.map((project: any) =>
                          renderRow(project, true),
                        )}
                      </React.Fragment>
                    ))}

                    {filteredProjects.length === 0 &&
                      filterDataByGroup.length === 0 && (
                        <Table.Tr>
                          <Table.Td colSpan={COLUMNS}>
                            <Text ta="center" c="dimmed" py="md">
                              No Personal use records found
                            </Text>
                          </Table.Td>
                        </Table.Tr>
                      )}
                  </Table.Tbody>
                </Table>
              </Table.ScrollContainer>

              <TablePagination
                page={page}
                totalPages={totalPages}
                onChange={setPage}
                totalItems={totalItems}
                pageSize={50}
              />
            </>
          )}
        </Stack>
      </Paper>

      <FilterModal
        isOpen={isModalOpen}
        onClose={() => setIsModalOpen(false)}
        onApply={handleApply}
        setCheckboxStates={setCheckboxStates}
        checkboxStates={checkboxStates}
        checkboxProps={checkboxProps}
      />

      {showEmployeeDetails && employeeDetails && (
        <EmployeeInfoModal
          employee={employeeDetails}
          onClose={() => setShowEmployeeDetails(false)}
        />
      )}
    </Layout>
  );
};
